import {Component, OnDestroy, OnInit} from '@angular/core';
import {routeAnimation} from '../../../route.animation';
import {ServerResponse} from '../../../_interfaces/server.response';
import {DateAdapter, MAT_DATE_FORMATS} from '@angular/material/core';
import {APP_DATE_FORMATS, CustomDateAdapter} from '../../../_pipes/angular2-material-datepicker-custom-date';
import {PlanningService} from '../../../_services/planning.service';
import {MatDialog} from '@angular/material';
import {addDays, eachDayOfInterval, endOfWeek, format, isSameDay, parseISO, startOfWeek} from 'date-fns';
import {nl} from 'date-fns/locale';
import {Planning} from '../../../_interfaces/planning/planning';
import {ModalPlanningViewComponent} from './modal-planning-view/modal-planning-view.component';

@Component({
	selector: 'app-planning-list',
	templateUrl: './planning-list.component.html',
	styleUrls: ['./planning-list.component.scss'],
	animations: [routeAnimation],
	providers: [
		{
			provide: DateAdapter, useClass: CustomDateAdapter
		},
		{
			provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS
		}
	]
})

export class PlanningListComponent implements OnInit, OnDestroy {

	view: 'month' | 'week' | 'day' = 'week';
	currentDate: Date = new Date();
	days: Date[] = [];
	events = [];

	eventsMap: { [key: string]: any[] } = {};

	constructor(public planningService: PlanningService,
				private dialogService: MatDialog) {
	}

	ngOnInit(): void {
		this.getPlanning();
	}

	ngOnDestroy(): void {
	}

	buildEventsMap(): void {
		this.eventsMap = {};

		for (const event of this.events) {
			const start: Date = new Date(event.start_at);
			let end: Date = new Date(event.end_at || event.start_at); // fallback if end missing

			if (end < start) {
				end = start; // make end same as start if end comes before start
			}

			const days: Date[] = eachDayOfInterval({start, end});

			for (const day of days) {
				const key: string = format(day, 'yyyy-MM-dd');

				if (!this.eventsMap[key]) {
					this.eventsMap[key] = [];
				}

				this.eventsMap[key].push(event);
			}
		}

		this.generateWeek();
	}

	getEventsForDay(date: Date): any[] {
		return this.eventsMap[format(date, 'yyyy-MM-dd')] || [];
	}

	hasEvents(date: Date): boolean {
		return this.getEventsForDay(date).length > 0;
	}

	changeWeek(amount: number): void {
		this.currentDate = addDays(this.currentDate, amount * 7);
		this.getPlanning();
	}

	generateWeek(): void {
		const start: Date = startOfWeek(this.currentDate, {weekStartsOn: 1});
		this.days = [];

		for (let i: number = 0; i < 7; i++) {
			this.days.push(addDays(start, i));
		}
	}

	getWeekRange(date: Date): string {
		const start: Date = startOfWeek(date, {weekStartsOn: 1});
		const end: Date = endOfWeek(date, {weekStartsOn: 1});

		const startFormatted: string = format(start, 'd. MMM', {locale: nl});
		const endFormatted: string = format(end, 'd. MMM yy', {locale: nl});

		return `${startFormatted} – ${endFormatted}`;
	}

	getDayFormat(date: Date): string {
		const formatted: string = format(date, 'EEEE, d MMMM yyyy', {locale: nl});

		return formatted.charAt(0).toUpperCase() + formatted.slice(1);
	}

	startAndEndNotInSameDay(event: Planning): boolean {
		return !isSameDay(parseISO(event.start_at), parseISO(event.end_at));
	}

	viewEvent(event: Planning): void {
		this.dialogService.open(ModalPlanningViewComponent, {
			data: event
		});
	}

	getPlanning(): void {
		this.planningService.getPlannings({
			start: format(startOfWeek(this.currentDate, {weekStartsOn: 1}), 'yyyy-MM-dd'),
		}).then((data: ServerResponse): void => {
			if (typeof data !== 'undefined' && typeof data.data !== 'undefined') {
				this.events = data.data.map((e: Planning) => ({
					...e,
					date: new Date(e.start_at)
				}));
			}

			this.buildEventsMap();
		});
	}
}
